import { Injectable, ErrorHandler } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, RouteConfigLoadEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface AppError {
  id: string;
  type: 'client' | 'server' | 'network' | 'auth';
  severity: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  details?: any;
  timestamp: Date;
  retryable: boolean;
  supportMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService implements ErrorHandler {
  private errors$ = new BehaviorSubject<AppError[]>([]);
  private isOffline$ = new BehaviorSubject<boolean>(false);
  
  // Common error messages with user-friendly translations
  private errorMessages: Record<string, { title: string; message: string; retryable: boolean }> = {
    // Network errors
    'NETWORK_ERROR': {
      title: 'Connection Error',
      message: 'Unable to connect to our servers. Please check your internet connection and try again.',
      retryable: true
    },
    'TIMEOUT': {
      title: 'Request Timeout',
      message: 'The request took too long to complete. Please try again.',
      retryable: true
    },
    
    // Authentication errors
    'UNAUTHORIZED': {
      title: 'Authentication Required',
      message: 'Please log in to continue.',
      retryable: false
    },
    'FORBIDDEN': {
      title: 'Access Denied',
      message: 'You don\'t have permission to access this resource.',
      retryable: false
    },
    'SESSION_EXPIRED': {
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again.',
      retryable: false
    },
    
    // Data errors
    'NOT_FOUND': {
      title: 'Not Found',
      message: 'The requested resource could not be found.',
      retryable: false
    },
    'VALIDATION_ERROR': {
      title: 'Invalid Data',
      message: 'Please check your input and try again.',
      retryable: false
    },
    'CONFLICT': {
      title: 'Data Conflict',
      message: 'This action conflicts with existing data. Please refresh and try again.',
      retryable: true
    },
    
    // Server errors
    'SERVER_ERROR': {
      title: 'Server Error',
      message: 'Something went wrong on our end. We\'re working to fix it.',
      retryable: true
    },
    'MAINTENANCE': {
      title: 'Under Maintenance',
      message: 'We\'re currently performing maintenance. Please try again later.',
      retryable: false
    },
    
    // Generic errors
    'UNKNOWN': {
      title: 'Unexpected Error',
      message: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
      retryable: true
    }
  };

  constructor(private router: Router) {
    // Monitor online/offline status
    this.setupOfflineDetection();
    // Auto-recover from stale lazy-chunk failures after a deploy (KAN-369)
    this.setupChunkReloadRecovery();
  }

  // KAN-369: A successful lazy-chunk load (Angular fires RouteConfigLoadEnd only
  // when a lazy route's chunk actually loaded) proves chunk serving is healthy
  // again, so we clear the one-shot reload guard. This lets a long-lived tab
  // recover from a SECOND deploy while never looping on a genuinely broken
  // deploy — a broken deploy never fires RouteConfigLoadEnd, so the guard stays
  // set and no further auto-reload happens.
  private setupChunkReloadRecovery(): void {
    // Defensive: some unit-test Router mocks omit `events`. In production the
    // real Router always exposes an events Observable.
    const events = this.router.events as Observable<unknown> | undefined;
    if (!events || typeof events.pipe !== 'function') {
      return;
    }
    events
      .pipe(filter((e): e is RouteConfigLoadEnd => e instanceof RouteConfigLoadEnd))
      .subscribe(() => this.clearChunkReloadGuard());
  }

  private setupOfflineDetection(): void {
    // Check initial online status
    this.isOffline$.next(!navigator.onLine);
    
    window.addEventListener('online', () => {
      this.isOffline$.next(false);
      this.addError({
        type: 'network',
        severity: 'info',
        title: 'Back Online',
        message: 'Your internet connection has been restored.',
        retryable: false
      });
      
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        const errors = this.errors$.value;
        const onlineError = errors.find(e => e.title === 'Back Online');
        if (onlineError) {
          this.dismissError(onlineError.id);
        }
      }, 3000);
    });

    window.addEventListener('offline', () => {
      this.isOffline$.next(true);
      this.addError({
        type: 'network',
        severity: 'warning',
        title: 'No Internet Connection',
        message: 'You are currently offline. Some features may not be available.',
        retryable: false
      });
      
      // Navigate to offline page for better UX
      if (this.router.url !== '/offline') {
        sessionStorage.setItem('preOfflineUrl', this.router.url);
        this.router.navigate(['/offline']);
      }
    });
    
    // Periodic connectivity check
    setInterval(() => {
      if (!navigator.onLine && !this.isOffline$.value) {
        this.isOffline$.next(true);
      } else if (navigator.onLine && this.isOffline$.value) {
        this.isOffline$.next(false);
      }
    }, 5000); // Check every 5 seconds
  }

  handleError(error: Error | HttpErrorResponse): void {
    console.error('Global error handler:', error);
    
    // Skip showing error toasts for optional endpoints that return 403
    if (error instanceof HttpErrorResponse && error.status === 403) {
      const optionalEndpoints = ['/categories', '/tags', '/analytics', '/reports'];
      const isOptionalEndpoint = optionalEndpoints.some(endpoint => 
        error.url && error.url.includes(endpoint)
      );
      
      if (isOptionalEndpoint) {
        console.log('Ignoring 403 error for optional endpoint:', error.url);
        return; // Don't show error toast for optional features
      }
    }
    
    let appError: Partial<AppError>;

    if (error instanceof HttpErrorResponse) {
      appError = this.handleHttpError(error);
    } else {
      appError = this.handleClientError(error);
    }

    this.addError(appError);
  }

  private handleHttpError(error: HttpErrorResponse): Partial<AppError> {
    let errorKey = 'UNKNOWN';
    let type: AppError['type'] = 'server';
    
    // Determine error type based on status code
    if (error.status === 0) {
      errorKey = 'NETWORK_ERROR';
      type = 'network';
    } else if (error.status === 401) {
      errorKey = 'UNAUTHORIZED';
      type = 'auth';
      // Redirect to login
      this.router.navigate(['/auth/login']);
    } else if (error.status === 403) {
      errorKey = 'FORBIDDEN';
      type = 'auth';
    } else if (error.status === 404) {
      errorKey = 'NOT_FOUND';
    } else if (error.status === 408) {
      errorKey = 'TIMEOUT';
      type = 'network';
    } else if (error.status === 409) {
      errorKey = 'CONFLICT';
    } else if (error.status === 422) {
      errorKey = 'VALIDATION_ERROR';
    } else if (error.status >= 500) {
      errorKey = 'SERVER_ERROR';
    }

    // Check for custom error code in response
    const customErrorCode = error.error?.error?.code;
    if (customErrorCode && this.errorMessages[customErrorCode]) {
      errorKey = customErrorCode;
    }

    const errorInfo = this.errorMessages[errorKey];
    
    // UI polish audit #3: a learner once saw 'Failed to load organization
    // details: K' — the trailing ": K" came from passing a one-character
    // backend error message straight to the toast. If the backend hands us
    // something that's not a real human-readable sentence (too short, or
    // just a code), fall back to our friendly default instead of leaking it.
    const backendMessage = error.error?.error?.message;
    const safeBackendMessage = isPresentableErrorMessage(backendMessage)
      ? backendMessage
      : null;

    return {
      type,
      severity: 'error',
      title: errorInfo.title,
      message: safeBackendMessage || errorInfo.message,
      details: error.error,
      retryable: errorInfo.retryable,
      supportMessage: this.getSupportMessage(error)
    };
  }

  private static readonly CHUNK_RELOAD_FLAG = 'cb_chunk_reload_attempted';

  private handleClientError(error: Error): Partial<AppError> {
    // Check if it's a stale lazy-chunk load failure (KAN-369). Detection is a
    // pure, exported predicate that covers both the legacy webpack phrasing and
    // the esbuild/native-ESM dynamic-import variants this project actually emits.
    if (isChunkLoadError(error?.message)) {
      this.attemptChunkReload();
      return {
        type: 'client',
        severity: 'warning',
        title: 'Update Available',
        message: 'A new version is available. Refreshing the page...',
        retryable: true
      };
    }
    
    return {
      type: 'client',
      severity: 'error',
      title: 'Application Error',
      message: 'An error occurred in the application. Please refresh the page and try again.',
      details: error.message,
      retryable: true
    };
  }

  private attemptChunkReload(): void {
    try {
      if (sessionStorage.getItem(ErrorHandlerService.CHUNK_RELOAD_FLAG)) {
        return;
      }
      sessionStorage.setItem(ErrorHandlerService.CHUNK_RELOAD_FLAG, '1');
      this.reloadPage();
    } catch {
      // sessionStorage may be unavailable (private mode, SSR) — skip
    }
  }

  private clearChunkReloadGuard(): void {
    try {
      sessionStorage.removeItem(ErrorHandlerService.CHUNK_RELOAD_FLAG);
    } catch {
      // sessionStorage may be unavailable (private mode, SSR) — nothing to clear
    }
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  private addError(error: Partial<AppError>): void {
    const fullError: AppError = {
      id: this.generateErrorId(),
      timestamp: new Date(),
      ...error
    } as AppError;

    const currentErrors = this.errors$.value;
    this.errors$.next([...currentErrors, fullError]);

    // Auto-dismiss info and success messages after 5 seconds
    if (fullError.severity === 'info' || fullError.severity === 'success') {
      setTimeout(() => {
        this.dismissError(fullError.id);
      }, 5000);
    }
  }

  getErrors(): Observable<AppError[]> {
    return this.errors$.asObservable();
  }

  getIsOffline(): Observable<boolean> {
    return this.isOffline$.asObservable();
  }

  dismissError(errorId: string): void {
    const currentErrors = this.errors$.value;
    this.errors$.next(currentErrors.filter(e => e.id !== errorId));
  }

  clearAllErrors(): void {
    this.errors$.next([]);
  }

  private generateErrorId(): string {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getSupportMessage(error: HttpErrorResponse): string {
    const errorId = this.generateErrorId();
    const timestamp = new Date().toISOString();
    
    return `If this problem persists, please contact support with the following information:
    Error ID: ${errorId}
    Timestamp: ${timestamp}
    Status: ${error.status}
    URL: ${error.url}`;
  }

  // Helper method to manually report errors
  reportError(title: string, message: string, severity: AppError['severity'] = 'error', retryable: boolean = false): void {
    this.addError({
      type: 'client',
      severity,
      title,
      message,
      retryable
    });
  }

  // Helper method to report successful actions as auto-dismissing toasts
  reportSuccess(title: string, message: string): void {
    this.addError({
      type: 'client',
      severity: 'success',
      title,
      message,
      retryable: false
    });
  }
}

/**
 * Decide whether a backend-provided error message is safe to show verbatim
 * in a user-facing toast.
 *
 * A real human-readable error message has at minimum:
 *   - 6+ trimmed characters (filters out "K", "OK", "err", single tokens)
 *   - at least one whitespace OR at least one ASCII letter sequence of 4+
 *
 * Otherwise the caller should fall back to its own friendly default rather
 * than rendering something like 'Failed to load organization details: K'
 * to a learner who has no idea what 'K' means.
 *
 * Exported for tests.
 */
export function isPresentableErrorMessage(msg: unknown): msg is string {
  if (typeof msg !== 'string') return false;
  const trimmed = msg.trim();
  if (trimmed.length < 6) return false;
  // Reject things that look like opaque short codes (e.g. "ERR_X", "E_AUTH")
  // — they're not helpful to a user without context.
  const hasSpace = /\s/.test(trimmed);
  const hasReasonablyLongWord = /[A-Za-z]{4,}/.test(trimmed);
  return hasSpace || hasReasonablyLongWord;
}

/**
 * Extract a user-presentable error message from a failed HTTP call, falling
 * back to a caller-supplied generic string.
 *
 * The real backend wraps validation failures in a
 * `{ success: false, error: { message, code } }` envelope. Angular's
 * HttpErrorResponse exposes that parsed body at `error.error`, so the helpful
 * server message lives at `error.error.error.message`. Legacy/mock shapes that
 * put the message directly at `error.error.message` are covered by the fallback
 * chain. Each candidate must pass `isPresentableErrorMessage` so we never leak
 * opaque junk (e.g. a bare "K" or a raw HTTP status line) — when nothing is
 * presentable, the caller's `fallback` is returned.
 *
 * This is the single source of truth for quiz create/update error banners
 * (KAN-389): the course-builder surfaces previously showed a generic
 * "Please try again." banner even though the API returned a specific
 * "Question 1 (multiple-choice): question text is required" message.
 *
 * Exported for tests.
 */
export function presentableApiErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: unknown } | null | undefined)?.error as
    | { message?: unknown; error?: { message?: unknown } }
    | undefined;
  const nested = body?.error?.message;
  if (isPresentableErrorMessage(nested)) return nested;
  const flat = body?.message;
  if (isPresentableErrorMessage(flat)) return flat;
  return fallback;
}

/**
 * Message fragments that identify a failed lazy-chunk / dynamic-import load.
 *
 * This project builds with Angular's esbuild builder
 * (`@angular-devkit/build-angular:application`), so lazy routes are native ES
 * modules named `chunk-XXXX.js` with content hashes — NOT numeric webpack
 * chunks. After a deploy, `s3 sync --delete` removes the OLD content-hashed
 * chunk; a stale tab that navigates to a lazy route then requests a chunk that
 * no longer exists, CloudFront serves the SPA `index.html` fallback (200,
 * `text/html`), and the browser refuses to execute HTML as a module.
 *
 * Each engine phrases that failure differently and NONE contains the legacy
 * webpack `'Loading chunk'` string, which is why the original narrow check
 * silently failed and left users on a dead click (KAN-369).
 *
 * Matched case-insensitively. Kept intentionally narrow so ordinary runtime
 * TypeErrors are NOT swallowed into a page reload.
 */
const CHUNK_LOAD_ERROR_PATTERNS: readonly string[] = [
  'loading chunk',                               // legacy webpack (back-compat)
  'failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module',   // Firefox
  'importing a module script failed',            // Safari
  'failed to load module script',                // MIME-type SPA-fallback variant
];

/**
 * Whether an error message indicates a stale/missing lazy-chunk load failure
 * that a full page reload (fetching a fresh index.html + chunk manifest) can
 * recover from.
 *
 * Safe for null / undefined / non-string / empty input (returns false).
 * Exported for tests.
 */
export function isChunkLoadError(message: unknown): boolean {
  if (typeof message !== 'string' || message.length === 0) return false;
  const lower = message.toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
}